import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiShoppingCart, FiPrinter, FiDownload, FiX, FiSettings, FiPercent, FiHome, FiCoffee, FiShoppingBag } from 'react-icons/fi';
import Layout from '../../components/Layout';
import MenuItems from './MenuItems';
import OrderCart from './OrderCart';
import ReceiptModal from './ReceiptModal';
import DiscountModal from './DiscountModal';
import menuItems from './data/menuItems.json';

const BillingAndOrders = ({ currency, setCurrency }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [showReceipt, setShowReceipt] = useState(false);
  const [orderType, setOrderType] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [error, setError] = useState('');

  // Get unique categories
  const categories = ['All', ...new Set(menuItems.map(item => item.category))];

  // Filter menu items
  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  // Add item to cart
  const addToCart = (item) => {
    const itemToAdd = selectedItem ? { 
      ...item, 
      specialInstructions,
      cartId: Date.now()
    } : {
      ...item,
      specialInstructions: '',
      cartId: Date.now()
    };

    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => 
        cartItem.id === item.id && 
        cartItem.specialInstructions === itemToAdd.specialInstructions
      );
      
      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.cartId === existingItem.cartId
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      return [...prevCart, { ...itemToAdd, quantity: 1 }];
    });

    setSpecialInstructions('');
    setSelectedItem(null);
    setShowCustomizeModal(false);
  };

  // Remove item from cart
  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  // Update item quantity
  const updateQuantity = (cartId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(cartId);
      return;
    }
    setCart(cart.map(item =>
      item.cartId === cartId ? { ...item, quantity: newQuantity } : item
    ));
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
    setDiscount(null);
    setError('');
  };

  // Open customize modal
  const openCustomizeModal = (item) => {
    setSelectedItem(item);
    setShowCustomizeModal(true);
  };

  // Apply discount
  const applyDiscount = (discountData) => {
    setDiscount(discountData);
    setShowDiscountModal(false);
  };

  // Remove discount
  const removeDiscount = () => {
    setDiscount(null);
  };

  const sendToKitchen = () => {
  if (!orderType) {
    setError('Please select an order type');
    return;
  }

  if (orderType === 'Dine In' && !tableNumber) {
    setError('Please enter a table number');
    return;
  }

  if (orderType === 'Take Out' && !customerName) {
    setError('Please enter customer name');
    return;
  }

  const newOrder = {
    id: Date.now(),
    orderNumber: `ORD-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    status: 'pending',
    orderType,
    tableNumber,
    customerName,
    items: [...cart],
    createdAt: new Date().toISOString(),
    discount: discount || null
  };

  // Get existing orders from localStorage with proper default value
  const existingOrdersJSON = localStorage.getItem('kitchenOrders');
  let existingOrders = [];
  
  try {
    existingOrders = existingOrdersJSON ? JSON.parse(existingOrdersJSON) : [];
  } catch (error) {
    console.error('Error parsing kitchen orders:', error);
    existingOrders = [];
  }

  const updatedOrders = [...existingOrders, newOrder];
  
  try {
    localStorage.setItem('kitchenOrders', JSON.stringify(updatedOrders));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    // Handle storage full error or other issues
  }

  // Clear the cart
  clearCart();
  setShowReceipt(false);
};

  // Generate receipt
  const generateReceipt = () => {
    if (!orderType) {
      setError('Please select an order type');
      return;
    }

    if (orderType === 'Dine In' && !tableNumber) {
      setError('Please enter a table number');
      return;
    }

    if (orderType === 'Take Out' && !customerName) {
      setError('Please enter customer name');
      return;
    }

    setError('');
    setShowReceipt(true);
  };

  return (
    <Layout currency={currency} setCurrency={setCurrency}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-800">Billing & Orders</h1>
          </div>
          <div className="relative">
            <button 
              className="flex items-center space-x-2 text-gray-800 hover:text-blue-600"
              onClick={() => document.getElementById('cart-sidebar').scrollIntoView()}
            >
              <FiShoppingCart size={20} />
              <span className="bg-gray-800 text-white rounded-full min-w-[70px] px-3 h-6 flex items-center justify-center text-sm font-medium">
                {cart.reduce((sum, item) => sum + item.quantity, 0)} items
              </span>
            </button>
          </div>
        </div>

        {/* Order Type */}
        <div className="flex items-center space-x-6 mb-6">
          <div className="flex items-center space-x-4">
            <span className="font-medium text-gray-800">Order Type:</span>
            <div className="flex items-center space-x-2">
              <button
                className={`flex items-center px-4 py-2 rounded-md space-x-2 ${orderType === 'Dine In' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800 border border-gray-200'}`}
                onClick={() => {
                  setOrderType('Dine In');
                  setError('');
                }}
              >
                <FiHome size={16} />
                <span>Dine In</span>
              </button>
              <button
                className={`flex items-center px-4 py-2 rounded-md space-x-2 ${orderType === 'Take Out' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800 border border-gray-200'}`}
                onClick={() => {
                  setOrderType('Take Out');
                  setError('');
                }}
              >
                <FiShoppingBag size={16} />
                <span>Take Out</span>
              </button>
            </div>
          </div>
          {orderType === 'Dine In' && (
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-800">Table #:</span>
              <input
                type="text"
                className="w-40 px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-gray-800"
                value={tableNumber}
                onChange={(e) => {
                  setTableNumber(e.target.value);
                  if (e.target.value) setError('');
                }}
                placeholder="Number"
              />
            </div>
          )}
          {orderType === 'Take Out' && (
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-800">Customer:</span>
              <input
                type="text"
                className="w-40 px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-gray-800"
                value={customerName}
                onChange={(e) => {
                  setCustomerName(e.target.value);
                  if (e.target.value) setError('');
                }}
                placeholder="Customer Name"
              />
            </div>
          )}
        </div>
        
        {/* Search and Categories */}
        <div className="flex justify-between items-center mb-6">
          <div className="relative w-1/3">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search menu items..."
              className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-gray-800 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {categories.map(category => (
              <button
                key={category}
                className={`px-4 py-2 rounded-md text-sm whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-gray-800 text-white'
                    : 'bg-white text-gray-800 border border-gray-200 hover:bg-gray-100'
                }`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Menu Items */}
          <MenuItems 
            filteredItems={filteredItems} 
            addToCart={addToCart}
            openCustomizeModal={openCustomizeModal}
          />

          {/* Order Cart */}
          <OrderCart 
            cart={cart}
            removeFromCart={removeFromCart}
            updateQuantity={updateQuantity}
            clearCart={clearCart}
            generateReceipt={generateReceipt}
            orderType={orderType}
            tableNumber={tableNumber}
            customerName={customerName}
            discount={discount}
            setShowDiscountModal={setShowDiscountModal}
            removeDiscount={removeDiscount}
            error={error}
          />
        </div>

        {/* Receipt Modal */}
        {showReceipt && (
          <ReceiptModal 
            cart={cart}
            setShowReceipt={setShowReceipt}
            orderType={orderType}
            tableNumber={tableNumber}
            customerName={customerName}
            discount={discount}
            sendToKitchen={sendToKitchen}
          />
        )}

        {/* Customize Modal */}
        {showCustomizeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-2">Customize {selectedItem?.name}</h2>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Special Instructions</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-gray-800"
                    rows="3"
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Any special requests or modifications..."
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setSpecialInstructions('');
                      setShowCustomizeModal(false);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => addToCart(selectedItem)}
                    className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700"
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Discount Modal */}
        {showDiscountModal && (
          <DiscountModal
            applyDiscount={applyDiscount}
            setShowDiscountModal={setShowDiscountModal}
            currentDiscount={discount}
          />
        )}
      </div>
    </Layout>
  );
};

export default BillingAndOrders;